import {
  ActionIcon,
  Anchor,
  Box,
  Chip,
  CopyButton,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';
import type { QaItem } from '@wwaave/shared/cms';
import { useEffect, useState } from 'react';

import { MarketingHero } from '../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../components/marketing/MarketingSection';
import { PublicShell } from '../components/marketing/PublicShell';
import { useCmsImage, useCmsSlot } from '../lib/cms';

type FAQEntry = QaItem;

export const Route = createFileRoute('/faq')({
  component: FAQPage,
});

function FAQPage() {
  // Wave C — FAQ items live in `cms_slots` (key 'faq.items'). When no
  // override is set, the registry default ships verbatim. Voice-linted via
  // the admin editor; structurally validated by validateSlotBody on save.
  const FAQ_ITEMS = useCmsSlot<'qa_list'>('faq.items');
  const heroEyebrow = useCmsSlot<'markdown'>('faq.hero_eyebrow');
  const heroTitle = useCmsSlot<'markdown'>('faq.hero_title');
  const heroLede = useCmsSlot<'markdown'>('faq.hero_lede');
  const { image: heroImg, ready: heroImgReady } = useCmsImage('faq.hero_image');
  const jumpEyebrow = useCmsSlot<'markdown'>('faq.jump_eyebrow');
  const jumpTitle = useCmsSlot<'markdown'>('faq.jump_title');
  const contactPrompt = useCmsSlot<'markdown'>('faq.contact_prompt');
  const contactEmail = useCmsSlot<'markdown'>('faq.contact_email');
  const contactDisputeLink = useCmsSlot<'markdown'>('faq.contact_dispute_link');
  const nextTitle = useCmsSlot<'markdown'>('faq.nextsteps_title');
  const [openId, setOpenId] = useState<string | null>(null);

  // Deep-link on first render: open whichever question matches the URL hash.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash.replace(/^#/, '');
    if (hash && FAQ_ITEMS.some((q) => q.id === hash)) {
      setOpenId(hash);
      // Defer the scroll until the panel opens, otherwise we land before the
      // accordion animates and the target is the wrong height.
      window.requestAnimationFrame(() => {
        const el = document.getElementById(`faq-${hash}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [FAQ_ITEMS]);

  const handleChipClick = (id: string) => {
    setOpenId(id);
    if (typeof window !== 'undefined') {
      // Update hash without reloading. requestAnimationFrame so the panel
      // opens before we scroll.
      window.history.replaceState(null, '', `#${id}`);
      window.requestAnimationFrame(() => {
        const el = document.getElementById(`faq-${id}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  return (
    <PublicShell>
      <MarketingHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        lede={heroLede}
        {...(heroImgReady ? { backgroundImage: heroImg.url } : {})}
        backgroundImageAlt={heroImg.alt}
        backgroundImageOpacity={heroImg.opacity ?? 0.18}
      />

      <MarketingSection eyebrow={jumpEyebrow} title={jumpTitle}>
        <Group gap="xs" wrap="wrap" mb="xl">
          {FAQ_ITEMS.map((item) => (
            <Chip
              key={item.id}
              checked={openId === item.id}
              onChange={() => handleChipClick(item.id)}
              variant="light"
              color="brand"
              size="md"
            >
              {item.chip}
            </Chip>
          ))}
        </Group>

        <Stack gap="md">
          {FAQ_ITEMS.map((item) => (
            <FAQCard
              key={item.id}
              entry={item}
              isOpen={openId === item.id}
              onToggle={() => setOpenId(openId === item.id ? null : item.id)}
            />
          ))}
        </Stack>

        <Group mt="xl" gap="md" align="center" wrap="wrap">
          <Text c="ink.7" size="sm">
            {contactPrompt}
          </Text>
          <Anchor href="mailto:hello@wwaave.com" c="brand.7" fw={500}>
            {contactEmail}
          </Anchor>
          <Text c="ink.5" aria-hidden>
            ·
          </Text>
          <Anchor component={Link} to="/dispute-resolution" c="brand.7" fw={500}>
            {contactDisputeLink}
          </Anchor>
        </Group>
      </MarketingSection>

      <MarketingNextSteps
        title={nextTitle}
        destinations={['/how-it-works', '/our-ethos', '/founding-venues']}
      />
    </PublicShell>
  );
}

function FAQCard({
  entry,
  isOpen,
  onToggle,
}: {
  entry: FAQEntry;
  isOpen: boolean;
  onToggle: () => void;
}) {
  // Note: cannot use SSR-safe location here — inside a SPA the URL is known
  // post-mount. The CopyButton fallback handles the case where the user
  // copies before the URL is computed.
  const deepLink =
    typeof window !== 'undefined'
      ? `${window.location.origin}/faq#${entry.id}`
      : `/faq#${entry.id}`;

  return (
    <Box
      id={`faq-${entry.id}`}
      style={{
        scrollMarginTop: 96,
        border: '1px solid var(--mantine-color-ink-1)',
        borderRadius: 12,
        backgroundColor: 'light-dark(#fff, var(--mantine-color-dark-6))',
        overflow: 'hidden',
        transition: 'box-shadow 160ms ease-out, border-color 160ms ease-out',
        boxShadow: isOpen ? '0 6px 24px color-mix(in srgb, var(--wwaave-brand-primary) 8%, transparent)' : 'none',
        borderColor: isOpen ? 'var(--mantine-color-brand-3)' : undefined,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-panel-${entry.id}`}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
          padding: '20px 24px',
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        <Title
          order={3}
          style={{
            flex: 1,
            fontFamily: 'var(--wwaave-font-display)',
            fontSize: 'clamp(1.15rem, 1.8vw, 1.45rem)',
            lineHeight: 1.3,
            letterSpacing: '-0.005em',
            fontWeight: 600,
            margin: 0,
            color: 'var(--mantine-color-ink-9)',
          }}
        >
          {entry.question}
        </Title>
        <Text
          aria-hidden
          c="ink.6"
          fw={500}
          style={{
            flexShrink: 0,
            fontSize: 22,
            lineHeight: 1,
            paddingTop: 4,
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            transition: 'transform 160ms ease-out',
            display: 'inline-block',
          }}
        >
          +
        </Text>
      </Box>

      {isOpen && (
        <Box
          id={`faq-panel-${entry.id}`}
          style={{
            padding: '4px 24px 22px',
          }}
        >
          <Stack gap="sm">
            {entry.answer.map((p, i) => (
              <Text
                key={i}
                c="ink.8"
                style={{
                  fontSize: 'clamp(0.95rem, 1.2vw, 1.05rem)',
                  lineHeight: 1.65,
                }}
              >
                {p}
              </Text>
            ))}
          </Stack>
          <Group gap={6} mt="md" align="center">
            <CopyButton value={deepLink} timeout={1500}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Link copied' : 'Copy link to this question'} withArrow>
                  <ActionIcon
                    variant="subtle"
                    color={copied ? 'teal' : 'gray'}
                    onClick={copy}
                    aria-label="Copy link to this question"
                  >
                    {copied ? '✓' : '#'}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
            <Text size="xs" c="ink.5">
              Permalink to this answer
            </Text>
          </Group>
        </Box>
      )}
    </Box>
  );
}
